import { forwardRef, useEffect, useState, useRef } from 'react';
import { useSnackbar } from 'notistack';
import {
  Slide,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  DialogContent,
  Stack,
  Button,
  DialogActions,
  MenuItem,
  Select,
  TextField,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled, alpha } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
// components
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
import DIOText from '../../components/DIOText';
import Label from '../../components/Label';
import InstantMatchingProfileStepper from './InstantMatchingProfileStepper';
import InstantGuidePopover from './InstantGuidePopover';
// charts
import AverageSalaryChart from '../offer/AverageSalaryChart';
// utils
import axios from '../../utils/axios';
// config
import { EVENT_ID } from '../../config';

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const SecondaryText = styled('span')(({ theme }) => {
  return {
    color: theme.palette.secondary.main,
  };
});

const InfoText = styled('span')(({ theme }) => {
  return {
    color: theme.palette.info.main,
  };
});

const BGAccordion = styled(Accordion)(({ theme }) => ({
  "&:hover": {
    border: '1px solid black',
  },
  padding: theme.spacing(1.5, 0),
  border: `1px solid ${theme.palette.grey[500_32]}`,
  borderRadius: Number(theme.shape.borderRadius),
}));

const NoBGAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  "&.Mui-focusVisible": {
    backgroundColor: "inherit",
  },
  height: 32,
  minHeight: '100% !important',
  padding: theme.spacing(0, 0, 0, 2),
}));

const SecondaryCaption = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));


const BGAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 2),
}));

const WeekBtn = styled(Button)(({theme}) => ({
  ...theme.typography.body1,
  minWidth: 36,
  minHeight: 36,
  width: 40,
  height: 40,
  padding: 0,
  margin: 0,
  borderRadius: 20,
  boxShadow: "none",
  border: `1px solid ${theme.palette.grey[500_32]}`,
}));

const TimeButton = styled(Button)(({theme}) => ({
  ...theme.typography.body1,
  padding: theme.spacing(2),
  margin: 0,
  color: theme.palette.text.secondary,
  boxShadow: theme.customShadows.card,
  [theme.breakpoints.down('md')]: {
    ...theme.typography.body2,
  },
}));

const WEEK_TITLE = ["월", "화", "수", "목", "금"];
const HOUR_TITLE = [
  { label: "오전 10시", value: "10:00", start: "10:00", end: "11:00" },
  { label: "오전 11시", value: "11:00", start: "11:00", end: "12:00" },
  { label: "정오 12시", value: "12:00", start: "12:00", end: "13:00" },
  { label: "오후 1시", value: "13:00", start: "13:00", end: "14:00" },
  { label: "오후 2시", value: "14:00", start: "14:00", end: "15:00" },
  { label: "오후 3시", value: "15:00", start: "15:00", end: "16:00" },
  { label: "오후 4시", value: "16:00", start: "16:00", end: "17:00" },
  { label: "오후 5시", value: "17:00", start: "17:00", end: "18:00" },
  { label: "오후 6시", value: "18:00", start: "18:00", end: "19:00" },
  { label: "오후 7시", value: "19:00", start: "19:00", end: "20:00" },
];

const WEEK_HOURS = Array.from(WEEK_TITLE).map((day) => ({day: day, hours: [...HOUR_TITLE]}));
const SALARY_LIMIT = 300;

// ------------------------------------------------------------

export default function InstantMatchingDialog({ application, isOpen, closeDialog, profile, mutateInstantProfile, handleAfterSubmit }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [start, setStart] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  // values
  const [hours, setHours] = useState('');
  const [salary, setSalary] = useState('');
  const [salaryError, setSalaryError] = useState(false);
  const [averageApplied, setAverageApplied] = useState(false);
  const [amount, setAmount] = useState({});
  const [detailIndex, setDetailIndex] = useState(0);
  const [expand, setExpand] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedDateTime, setSelectedDateTime] = useState({});
  const [appearPopover, setAppearPopover] = useState(false);

  // submit
  const [isSubmit, setIsSubmit] = useState(false);
  const isEdit = !!profile?.id;

  const smUp = useResponsive('up', 'sm');
  const availableHour = Math.min(parseInt(user?.crew?.limit_working_hours_per_week || 0), 15);
  const hoursOptions = availableHour ? Array.from(Array(availableHour).keys()).map((i) => availableHour - i) : [];

  const toggleAccordion = () => {
    setExpand((prev) => !prev);
  };

  const toggleDate = (dateStr) => {
    if (selectedDate === dateStr) {
      setSelectedDate('');
    } else {
      setSelectedDate(dateStr);
    }
  };

  const selectTime = (item) => {
    const newDict = {...selectedDateTime};
    if (newDict[selectedDate]) {
      if (newDict[selectedDate].find((d) => d.value === item.value)) {
        newDict[selectedDate] = newDict[selectedDate].filter((d) => d.value !== item.value);
      } else {
        newDict[selectedDate].push(item)
      }
    } else {
      newDict[selectedDate] = [item]
    }

    if (newDict[selectedDate].length === 0) {
      delete newDict[selectedDate];
    } else {
      newDict[selectedDate].sort((a, b) => a.value > b.value ? 1 : -1);
    }
    setSelectedDateTime(newDict);
  };

  const checkTime = () => {
    if (Object.keys(selectedDateTime).length < 3) {
      enqueueSnackbar('최소 3개 요일을 선택해주세요', { variant: 'error' });
    } else {
      const timeRange = Object.keys(selectedDateTime).map((d) => {
        return {
          date: d,
          intervals: selectedDateTime[d],
        }
      });
      return true;
    }
  }

  const checkAverageSalary = async () => {
    if (!averageApplied) {
      if (!hours) {
        enqueueSnackbar("업무 가능한 시간을 입력해주세요", { variant: "warning" });
      }
      else if (hours < 10 || hours > 20) {
        enqueueSnackbar("평균 인건비를 적용할 수 없는 업무 시간입니다. 직접 입력해주세요", { variant: "warning" });
      } else {
        const detail = amount?.details?.[detailIndex];
        if (hours < 16) {
          setSalary(detail?.amount_10_15);
        } else {
          setSalary(detail?.amount_16_20);
        }
        enqueueSnackbar("평균 인건비가 적용되었습니다");
        setAverageApplied(true);
      }
    } else {
      setAverageApplied(false);
    }
  };

  const handleSalary = async (e) => {
    setSalary(e.target.value); // 화면에 입력받고 표시하기 위한 값
    setAverageApplied(false);  
    setSalaryError(e.target.value > SALARY_LIMIT);
  };

  const goPrev = () => {
    setAppearPopover(false);
    if (step === 0) {
      setStart(true);
      return ;
    }
    setStep(step - 1);
  }

  const sendProfile = async () => {
    try {
      setIsSubmit(true);
      const sendResponse = await axios.post('api/crews/instant-matching/', {
        salary,
        working_hours: hours,
        available_days: selectedDateTime,
      });
      setIsSubmit(false);
      enqueueSnackbar('즉시 매칭 프로필이 등록되었습니다', { variant: 'lightning' });
      mutateInstantProfile();
      closeDialog();
      handleAfterSubmit();
    } catch (e) {
      console.error(e);
      setIsSubmit(false);
      if (e.response && e.response.status === 400) {
        enqueueSnackbar(e.response.data.message, { variant: 'warning' });
      }
    }
  }

  const nextStep = () => {
    if (step === 2) {
      if (checkTime()) {
        sendProfile();
      }
      return ;
    }
    if (step === 1) {
      if (!salary || salary <= 0) {
        enqueueSnackbar("인건비를 입력해주세요", { variant: "error" });
        return ;
      }
      if (salary > SALARY_LIMIT) {
        enqueueSnackbar("인건비는 300만원까지 입력 가능합니다", { variant: "error" });
        return ;
      }
    }
    if (step === 0) {
      if (!hours) {
        enqueueSnackbar("업무 시간을 선택해주세요", { variant: "error" });
        return ;
      }
    }
    setAppearPopover(false);
    setStep(step + 1);
  }

  const goto = (step) => {
    setStep(step);
  }

  const startRegistration = () => {
    setStart(false);
  }

  const handleChangeHours = (e) => {
    setHours(e.target.value);
  }

  const handlePopover = () => {
    setTimeout(() => {
      if (hours) return; 
      else setAppearPopover(true)
    }, 300);
  }

  useEffect(() => {
    if (!isOpen) return ;

    const initialize = async () => {
      try {
        const appResponse = await axios.get('api/crews/average-amount/');
        setAmount(appResponse.data);
        appResponse.data.details.forEach((detail, i) => {
          if (detail.max_years >= application?.years_of_experience && detail.min_years <= application?.years_of_experience) {
            setDetailIndex(i);
          }
        })
      } catch (err) {
        console.error(err);
      }
    };

    initialize();
  }, [isOpen, application]);

  return (
    <Dialog
      open={isOpen} fullScreen={!smUp && !start} TransitionComponent={Transition} maxWidth="sm" fullWidth
    >
      {start && !isEdit && (
        <>
        {/* 등록! 이면 안내부터 */}
        <DialogContent
          sx={{
            p: { xs: 3, sm: 4 },
          }}
        >
          {!smUp && (
          <AppBar position="relative" color="transparent" sx={{ boxShadow: 'none' }}>
            <Toolbar sx={{ px: '0 !important' }}>
              <IconButton color="inherit" edge="start" onClick={closeDialog}>
                <Iconify icon="ic:chevron-left" width={32} height={32} />
              </IconButton>
            </Toolbar>
          </AppBar>
          )}
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircleLightning />
            <Typography variant="h4">
              <SecondaryText>즉시 매칭</SecondaryText> 프로필 작성하기
            </Typography>
            {smUp && (
            <IconButton onClick={closeDialog} edge="end" sx={{ ml: 'auto !important' }}>
              <Iconify icon={'ic:close'} />
            </IconButton>
            )}
          </Stack>
          <Typography sx={{ mt: 1.5 }}>
            프로필을 작성하고, 즉시 매칭 크루가 되세요!
          </Typography>
          <Box
            sx={{
              mt: { xs: 4, sm: 5 },
              p: 3,
              backgroundColor: "grey.300",
              borderRadius: 3,
            }}
          >
            <Stack spacing={2} sx={{ width: 1 }} alignItems="center">
              <Typography variant='h6'>
                <SecondaryText>즉시 매칭</SecondaryText> 크루 혜택!
              </Typography>
              <Box
                sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 2, width: 1 }}
              >
                <Stack direction="row" spacing={1}>
                  <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "oldprimary.main" }} />
                  <Typography variant='subtitle2'>
                    인건비 협의 과정 없이 고정가로 계약!
                  </Typography>
                </Stack>
              </Box>
              <Box
                sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 2, width: 1 }}
              >
                <Stack direction="row" spacing={1}>
                  <SvgIconStyle src={`/icons/icon-building-mono.svg`} sx={{ color: "info.main" }} />
                  <Typography variant='subtitle2'>
                    디오가 선별한 젠틀한 기업만 매칭!
                  </Typography>
                </Stack>
              </Box>
              <Box
                sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 2, width: 1 }}
              >
                <Stack direction="row" spacing={1}>
                  <SvgIconStyle src={`/icons/icon-lightning-mono.svg`} sx={{ color: "warning.light" }} />
                  <Typography variant='subtitle2'>
                    가장 우선순위 높게 - 빠르게 매칭!
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ m: { xs: 0, sm: 1 } }}
        >
          <Button
            id={EVENT_ID.crewInstantMatchingProfileStart}
            variant="contained" size="large" fullWidth onClick={startRegistration}>
            프로필 작성 시작하기
          </Button>
        </DialogActions>
        </>
      )}
      {start && isEdit && (
        <>
        <DialogContent
          sx={{
            p: { xs: 3, sm: 4 },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <CircleLightning />
            <Typography variant="h4">
              <SecondaryText>즉시 매칭</SecondaryText> 프로필 상세
            </Typography>
          </Stack>

          <Stack spacing={3}>

            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: "secondary.main" }} />
                <Typography variant="h6">
                  주당 업무 가능 시간
                </Typography>
              </Stack>
              <Typography sx={{ pl: 4, whiteSpace: 'pre-wrap' }} variant="body1">
                주 {profile?.working_hours}시간
              </Typography>
            </Stack>

            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "secondary.main" }} />
                <Typography variant="h6">
                  4주 인건비
                </Typography>
              </Stack>
              <Typography sx={{ pl: 4, whiteSpace: 'pre-wrap' }} variant="body1">
                {profile?.salary}만원 <SecondaryCaption>4주(28일) 기준</SecondaryCaption>
              </Typography>
            </Stack>

            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic_videocam.svg`} sx={{ color: "secondary.main" }} />
                <Typography variant="h6">
                  미팅 가능 시간
                </Typography>
              </Stack>
              <Stack
                sx={{
                  ml: '20px !important',
                  backgroundColor: (theme) => theme.palette.grey[100],
                  borderRadius: 1.5,
                  p: 3,
                }}
                spacing={1}
              >
                {Object.keys(profile?.available_days).sort((a, b) => WEEK_TITLE.indexOf(a) > WEEK_TITLE.indexOf(b) ? 1 : -1).map((date, index) => {
                  if (profile?.available_days[date].length === 0) {
                    return null;
                  }
                  return (
                    <Stack key={index} direction="row" spacing={1}>
                      <Typography variant={smUp ? "body2" : "caption"} sx={{ minWidth: "55px" }}>
                        <b>매주 {date}요일</b>
                      </Typography>
                      <Typography variant={smUp ? "body2" : "caption"} sx={{ wordBreak: "keep-all" }}>
                        {profile?.available_days[date].map((item) => `${Number.parseInt(item.start.slice(0, 2))}시`).join(', ')}
                      </Typography>
                    </Stack>
                  )
                })}
              </Stack>
            </Stack>

          </Stack>

        </DialogContent>
        <DialogActions
          sx={{ m: { xs: 0, sm: 1 } }}
        >
          <Button
            id={EVENT_ID.crewInstantMatchingProfileEdit}
            variant="text" size="large" onClick={startRegistration} sx={{ px: 4 }}>
            수정하기
          </Button>
          <Button
            id={EVENT_ID.crewInstantMatchingProfileOK}
            variant="contained" size="large" onClick={closeDialog} sx={{ px: 4 }}>
            확인
          </Button>
        </DialogActions>
        </>
      )}
      {!start && (
        <>
        <DialogContent
          sx={{
            minHeight: { xs: 'auto', sm: '500px' },
            p: { xs: 3, sm: 4 },
          }}
        >
          <AppBar position="relative" color="transparent" sx={{ boxShadow: 'none' }}>
            <Toolbar sx={{ px: '0 !important' }}>
              <IconButton color="inherit" edge="start" onClick={goPrev}>
                <Iconify icon="ic:chevron-left" width={32} height={32} />
              </IconButton>
              <InstantMatchingProfileStepper step={step} goto={goto} />
              <IconButton color="inherit" edge="end" onClick={closeDialog} sx={{ visibility: "hidden"}}>
                <Iconify icon="ic:close" width={24} height={24} />
              </IconButton>
            </Toolbar>
          </AppBar>
          {/* step 0 */}
          {step === 0 && (
          <Slide direction="left" in={step === 0} timeout={300} addEndListener={handlePopover}>
            <Stack sx={{ mt: 5 }}>
              <Typography variant="h4" gutterBottom>
                주당 업무 가능 시간 설정하기
              </Typography>
              <Typography variant="subtitle1">
                계약할 수 있는 최대 업무 시간: <InfoText>주 {availableHour}시간</InfoText>
              </Typography>
              <Typography variant={smUp ? "body2" : "caption"} sx={{ color: "text.secondary" }}>
                즉시 매칭은 주당 최대 15시간까지만 계약하실 수 있습니다.
              </Typography>
              <Select
                value={hours}
                onChange={handleChangeHours}
                sx={{ mt: 3 }}
              >
                {hoursOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    주 {option}시간
                    {option === 10 && (
                      <Label sx={{ ml: 1 }} variant="filled" color="info">추천</Label>
                    )}
                  </MenuItem>
                ))}
              </Select>
              <InstantGuidePopover sx={{ mt: 2 }} openStart={appearPopover} />
            </Stack>
          </Slide>
          )}
          {/* step 1 */}
          {step === 1 && (
          <Slide direction="left" in={step === 1} timeout={300}>
            <Stack sx={{ mt: 5 }}>
              <Typography variant="h4" gutterBottom>
                4주 인건비 설정하기
              </Typography>
              <Typography variant="subtitle1">
                <InfoText>총 {hours*4}시간(4주) 기준</InfoText>으로 금액을 설정해주세요.
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                주 {hours}시간 X 4주 = 총 {hours*4}시간
              </Typography>
              <BGAccordion expanded={expand}
                sx={{
                  borderColor: salaryError ? 'error.main' : 'primary.main',
                  '&:hover': {
                    borderColor: salaryError ? 'error.main' : 'primary.main',
                  }
                }}
              >
                <NoBGAccordionSummary
                  expandIcon={<IconButton onClick={toggleAccordion}>
                    <ExpandMoreIcon />
                  </IconButton>
                  }
                >
                  <TextField
                    variant="standard"
                    fullWidth
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        e.preventDefault();
                      }
                    }}
                    value={salary}
                    onChange={(e) => handleSalary(e)}
                    placeholder="000"
                    InputProps={{
                      endAdornment: <InputAdornment position="end" sx={{ mr: 1 }}>만원</InputAdornment>,
                      disableUnderline: true,
                    }}
                  />
                </NoBGAccordionSummary>
                <BGAccordionDetails>
                  <AverageSalaryChart application={application} amount={amount} detailIndex={detailIndex} inModal />
                  <Stack display="flex" sx={{ width: 1 }}>
                    <FormControlLabel
                      sx={{
                        mx: 0,
                        justifyContent: 'flex-end',
                        '& .MuiFormControlLabel-label' : {color: (theme) => theme.palette.grey[600], fontWeight: 'bold' },
                      }}
                      control={
                        <Checkbox checked={averageApplied} onClick={() => checkAverageSalary()}/>
                      }
                      label="평균 인건비 적용하기"
                    />
                  </Stack>
                  <Button
                    size="medium"
                    sx={{ color: "text.secondary" }}
                    endIcon={<Iconify icon={"eva:external-link-outline"} width={16} height={16} />}
                    onClick={() => window.open('https://dio.oopy.io/faq-price', '_blank', 'noopener,noreferrer')}
                  >
                    <DIOText /> 평균 인건비 확인하기
                  </Button>
                </BGAccordionDetails>
              </BGAccordion>
              {salaryError ? (
                <Stack sx={{ ml: 1, mt: 2 }} direction="row" spacing={.5} alignItems="center">
                  <Iconify icon="eva:alert-triangle-fill" width={20} height={20} color="error.main" />
                  <Typography variant="body2" sx={{ color: "error.main" }}>
                    즉시 매칭은 <b>최대 300만원까지</b>만 설정 가능합니다. 
                  </Typography>
                </Stack>
               ) : (
                <InstantGuidePopover sx={{ ml: 1, mt: 2 }} bluePosition='second' />
              )}
            </Stack>
          </Slide>
          )}
          {step === 2 && (
          <Slide direction="left" in={step === 2} timeout={300}>
            <Stack sx={{ mt: 5 }}>
              <Typography variant="h4" gutterBottom>
                미팅 가능 시간 설정하기
              </Typography>
              <Typography variant="body1">
                회사와 겹치는 시간대가 생기도록 <br/>
                최대한 많은 요일과 시간대를 선택해주세요.
              </Typography>

              {/* weekdays & time */}
              <Stack
                sx={{
                  mt: 4,
                  width: 1,
                }}
              >
                {/* weekdays */}
                <Box 
                  sx={{
                    width: 1,
                    mx: 'auto',
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(5, 1fr)',
                    },
                  }}
                >
                  {WEEK_TITLE.map((item, index) => (
                    <WeekBtn
                      key={index}
                      sx={{
                        mx: 'auto',
                        ...(selectedDate === item) && {
                          fontWeight: 'bold',
                          border: (theme) => `1px solid ${theme.palette.oldprimary.main}`,
                          color: (theme) => theme.palette.oldprimary.main,
                          backgroundColor: (theme) => alpha(theme.palette.oldprimary.main, 0.08),
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.oldprimary.dark, 0.32),
                          },
                        }
                      }}
                      onClick={() => toggleDate(item)}
                    >
                      {item}
                    </WeekBtn>
                  ))}
                </Box>

                {/* time buttons */}
                <Box
                  sx={{
                    mt: 3,
                    width: 1,
                    mx: 'auto',
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: {
                      xs: 'repeat(3, 1fr)',
                      sm: 'repeat(4, 1fr)',
                    },
                  }}
                >
                  {selectedDate && WEEK_HOURS.find((item) => item.day === selectedDate)?.hours.map((item, index) => (
                    <TimeButton
                      key={index}
                      onClick={() => selectTime(item)}
                      sx={{
                        ...(selectedDateTime?.[selectedDate]?.find((d) => d.value === item.value)) && {
                          color: (theme) => theme.palette.common.white,
                          backgroundColor: "oldprimary.main",
                          fontWeight: 700,
                          '&:hover': {
                            backgroundColor: "oldprimary.dark",
                          },
                        }
                      }}
                      variant={(item.selected) ? "contained" : "text"}
                    >
                      {item.label}
                    </TimeButton>
                  ))}
                </Box>
              </Stack>

              {Object.keys(selectedDateTime).length > 0 && (
              <>
                <Typography variant={smUp ? "subtitle1" : "subtitle2"} paragraph sx={{ mt: 4 }}>
                  선택한 시간
                </Typography>
                <Stack
                  sx={{
                    backgroundColor: (theme) => theme.palette.grey[100],
                    borderRadius: 1.5,
                    px: 2.5,
                    py: 3,
                  }}
                  spacing={1}
                >
                  {Object.keys(selectedDateTime).sort((a, b) => WEEK_TITLE.indexOf(a) > WEEK_TITLE.indexOf(b) ? 1 : -1).map((date, index) => {
                    if (selectedDateTime[date].length === 0) {
                      return null;
                    }
                    return (
                      <Stack key={index} direction="row" spacing={1}>
                        <Typography variant={smUp ? "body2" : "caption"} sx={{ minWidth: "55px" }}>
                          <b>매주 {date}요일</b>
                        </Typography>
                        <Typography variant={smUp ? "body2" : "caption"} sx={{ wordBreak: "keep-all" }}>
                          {selectedDateTime[date].map((item) => `${Number.parseInt(item.start.slice(0, 2))}시`).join(', ')}
                        </Typography>
                      </Stack>
                    )
                  })}
                </Stack>
              </>
              )}
            </Stack>
          </Slide>
          )}
        </DialogContent>
        <DialogActions
          sx={{ m: { xs: 0, sm: 1 } }}
        >
        <LoadingButton
          disabled={salaryError}
          id={isEdit ? `${EVENT_ID.crewInstantMatchingProfileEditStep}-${step}` : `${EVENT_ID.crewInstantMatchingProfileStep}-${step}`}
          variant="contained" size="large" fullWidth onClick={nextStep} loading={isSubmit}>
          {step === 2 ? "완료" : "다음"}
        </LoadingButton>
      </DialogActions>
      </>
      )}
    </Dialog>
  )
}

function CircleLightning () {
  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 2,
        backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.08),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <SvgIconStyle src={`/icons/icon-lightning-mono.svg`} sx={{ width: 20, height: 20, color: "secondary.main" }} />
    </Box>
  )
}
